import { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { format, addDays, differenceInCalendarDays, startOfDay, isBefore, isAfter, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, CalendarRange, Tag, Inbox } from "lucide-react";
import { cn } from "@/lib/utils";
import { dotFor } from "@/lib/boardColors";
import { toast } from "sonner";

const DAY_WIDTH = 36;        // px per day on the timeline
const ROW_HEIGHT = 40;       // px per card row
const HEADER_HEIGHT = 56;    // px for the date header
const SIDEBAR_WIDTH = 260;   // px for the left-side card list
const DEFAULT_RANGE_DAYS = 30;
const NEW_TASK_DURATION = 3; // days assigned when a card without dates is dropped onto the timeline

function toIsoDay(d) {
  // Truncate to YYYY-MM-DDT00:00:00Z so backend stores stable day strings.
  const dt = startOfDay(d);
  return new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate())).toISOString();
}

function parseCardDate(v) {
  if (!v) return null;
  try { return startOfDay(parseISO(v)); } catch { return null; }
}

export default function BoardSprints({ boardId, cards, labels, lists, onOpenCard }) {
  const qc = useQueryClient();
  const scrollerRef = useRef(null);

  // Anchor date drives the left edge of the visible window.
  const today = useMemo(() => startOfDay(new Date()), []);
  const [anchor, setAnchor] = useState(today);
  const [rangeDays, setRangeDays] = useState(DEFAULT_RANGE_DAYS);

  // Group scheduled cards by list (ordered by list.position, then card.position).
  const { scheduled, unscheduled, listOrder } = useMemo(() => {
    const listsById = {};
    const order = [];
    const sortedLists = [...lists].sort((a, b) => a.position - b.position);
    for (const l of sortedLists) {
      listsById[l.list_id] = { list: l, cards: [] };
      order.push(l.list_id);
    }
    const unsched = [];
    for (const c of cards) {
      const start = parseCardDate(c.start_date);
      const due = parseCardDate(c.due_date);
      if (start && due && !isBefore(due, start)) {
        if (listsById[c.list_id]) listsById[c.list_id].cards.push({ ...c, _start: start, _due: due });
      } else if (start || due) {
        // Only one date set — treat as 1-day task on that date.
        const day = due || start;
        if (listsById[c.list_id]) listsById[c.list_id].cards.push({ ...c, _start: day, _due: day });
      } else {
        unsched.push(c);
      }
    }
    Object.values(listsById).forEach(g => g.cards.sort((a, b) => a.position - b.position));
    return { scheduled: listsById, unscheduled: unsched, listOrder: order };
  }, [cards, lists]);

  // Mutation: patch one card's start/due dates.
  const updateDates = useMutation({
    mutationFn: async ({ card_id, start_date, due_date }) =>
      (await api.patch(`/cards/${card_id}`, { start_date, due_date })).data,
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ["board-full", boardId] });
      const prev = qc.getQueryData(["board-full", boardId]);
      qc.setQueryData(["board-full", boardId], (old) => {
        if (!old) return old;
        return {
          ...old,
          cards: old.cards.map(c =>
            c.card_id === vars.card_id
              ? { ...c,
                  start_date: vars.start_date === undefined ? c.start_date : vars.start_date,
                  due_date:   vars.due_date   === undefined ? c.due_date   : vars.due_date }
              : c
          ),
        };
      });
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["board-full", boardId], ctx.prev);
      toast.error("No se pudo actualizar la fecha");
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["board-full", boardId] }),
  });

  // Timeline days array based on anchor + rangeDays
  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < rangeDays; i++) arr.push(addDays(anchor, i));
    return arr;
  }, [anchor, rangeDays]);

  const totalCardRows = useMemo(
    () => listOrder.reduce((sum, lid) => sum + 1 /* group header */ + scheduled[lid].cards.length, 0),
    [listOrder, scheduled]
  );
  const timelineHeight = totalCardRows * ROW_HEIGHT;

  // Scroll to "today" on mount and whenever rangeDays/anchor reset to today.
  useEffect(() => {
    if (!scrollerRef.current) return;
    const offset = differenceInCalendarDays(today, anchor) * DAY_WIDTH;
    scrollerRef.current.scrollLeft = Math.max(0, offset - 80);
  }, [anchor, today]);

  // ---------- Bar drag (move + resize) ----------
  const dragRef = useRef(null);
  const onPointerDown = useCallback((e, card, mode /* 'move' | 'resize-l' | 'resize-r' */) => {
    e.preventDefault();
    e.stopPropagation();
    const containerLeft = scrollerRef.current?.getBoundingClientRect().left || 0;
    const scrollLeft = scrollerRef.current?.scrollLeft || 0;
    dragRef.current = {
      cardId: card.card_id,
      mode,
      originStart: card._start,
      originDue: card._due,
      pointerStartX: e.clientX,
      containerLeft,
      scrollLeft,
      ghostStart: card._start,
      ghostDue: card._due,
    };
    document.body.style.cursor = mode === "move" ? "grabbing" : "ew-resize";
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const deltaPx = e.clientX - d.pointerStartX;
    const deltaDays = Math.round(deltaPx / DAY_WIDTH);
    if (d.mode === "move") {
      d.ghostStart = addDays(d.originStart, deltaDays);
      d.ghostDue   = addDays(d.originDue, deltaDays);
    } else if (d.mode === "resize-l") {
      const ns = addDays(d.originStart, deltaDays);
      d.ghostStart = isAfter(ns, d.originDue) ? d.originDue : ns;
      d.ghostDue   = d.originDue;
    } else if (d.mode === "resize-r") {
      const nd = addDays(d.originDue, deltaDays);
      d.ghostStart = d.originStart;
      d.ghostDue   = isBefore(nd, d.originStart) ? d.originStart : nd;
    }
    // Optimistically reflect new position via inline style on the dragged bar.
    const el = document.querySelector(`[data-bar-id="${d.cardId}"]`);
    if (el) {
      const left = differenceInCalendarDays(d.ghostStart, anchor) * DAY_WIDTH;
      const width = (differenceInCalendarDays(d.ghostDue, d.ghostStart) + 1) * DAY_WIDTH;
      el.style.transform = `translateX(${left}px)`;
      el.style.width = `${Math.max(DAY_WIDTH * 0.6, width)}px`;
    }
  }, [anchor]);

  const onPointerUp = useCallback(() => {
    const d = dragRef.current;
    document.body.style.cursor = "";
    if (!d) return;
    const movedStart = differenceInCalendarDays(d.ghostStart, d.originStart) !== 0;
    const movedDue   = differenceInCalendarDays(d.ghostDue, d.originDue) !== 0;
    if (movedStart || movedDue) {
      updateDates.mutate({
        card_id: d.cardId,
        start_date: movedStart ? toIsoDay(d.ghostStart) : undefined,
        due_date:   movedDue   ? toIsoDay(d.ghostDue)   : undefined,
      });
    }
    dragRef.current = null;
  }, [updateDates]);

  useEffect(() => {
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  // ---------- Schedule an unscheduled card by clicking a target day ----------
  const scheduleCard = (card, startDay) => {
    const start = startOfDay(startDay);
    const due = addDays(start, NEW_TASK_DURATION - 1);
    updateDates.mutate({
      card_id: card.card_id,
      start_date: toIsoDay(start),
      due_date: toIsoDay(due),
    });
    toast.success(`"${card.title}" programada ${format(start, "PPP", { locale: es })}`);
  };

  const goToday = () => setAnchor(today);
  const goPrev  = () => setAnchor((a) => addDays(a, -7));
  const goNext  = () => setAnchor((a) => addDays(a, 7));

  return (
    <div className="h-full flex flex-col" data-testid="board-sprints">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 md:px-6 py-3 border-b border-white/40 dark:border-white/10 bg-white/30 dark:bg-slate-900/30 backdrop-blur-md">
        <div className="inline-flex items-center gap-1 glass-card rounded-full p-0.5">
          <button onClick={goPrev} data-testid="sprints-prev" className="h-8 w-8 grid place-items-center rounded-full hover:bg-white/60 dark:hover:bg-white/10" aria-label="Semana anterior">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button onClick={goToday} data-testid="sprints-today" className="px-3 h-8 rounded-full text-xs font-medium hover:bg-white/60 dark:hover:bg-white/10">Hoy</button>
          <button onClick={goNext} data-testid="sprints-next" className="h-8 w-8 grid place-items-center rounded-full hover:bg-white/60 dark:hover:bg-white/10" aria-label="Semana siguiente">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <span className="text-sm font-medium ml-2 hidden md:inline">
          {format(anchor, "d MMM", { locale: es })} → {format(addDays(anchor, rangeDays - 1), "d MMM yyyy", { locale: es })}
        </span>
        <div className="ml-auto inline-flex items-center gap-1 glass-card rounded-full p-0.5">
          {[
            { v: 14, lbl: "2 sem" },
            { v: 30, lbl: "1 mes" },
            { v: 60, lbl: "2 meses" },
            { v: 90, lbl: "3 meses" },
          ].map(o => (
            <button
              key={o.v}
              onClick={() => setRangeDays(o.v)}
              data-testid={`sprints-range-${o.v}`}
              aria-pressed={rangeDays === o.v}
              className={cn(
                "px-3 h-8 rounded-full text-xs font-medium transition",
                rangeDays === o.v ? "bg-blue-500 text-white shadow-sm" : "hover:bg-white/60 dark:hover:bg-white/10"
              )}
            >
              {o.lbl}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sticky left sidebar with card titles */}
        <div className="flex-none border-r border-white/40 dark:border-white/10 bg-white/40 dark:bg-slate-900/40 backdrop-blur-md overflow-y-auto kanban-scroll" style={{ width: SIDEBAR_WIDTH }}>
          <div style={{ height: HEADER_HEIGHT }} className="border-b border-white/40 dark:border-white/10 flex items-center px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
            Tareas
          </div>
          {listOrder.map(lid => {
            const grp = scheduled[lid];
            return (
              <div key={lid}>
                <div style={{ height: ROW_HEIGHT }} className="px-4 flex items-center text-[11px] uppercase tracking-wider font-semibold text-blue-600 dark:text-blue-300 border-b border-white/20 dark:border-white/5 bg-white/30 dark:bg-slate-900/30">
                  {grp.list.name}
                  <span className="ml-1.5 text-muted-foreground normal-case font-normal">({grp.cards.length})</span>
                </div>
                {grp.cards.map(c => (
                  <button
                    key={c.card_id}
                    onClick={() => onOpenCard?.(c.card_id)}
                    data-testid={`sprints-row-${c.card_id}`}
                    style={{ height: ROW_HEIGHT }}
                    className="w-full text-left px-4 truncate text-sm border-b border-white/20 dark:border-white/5 hover:bg-white/50 dark:hover:bg-white/5 transition flex items-center gap-2"
                  >
                    <span className={cn("h-2 w-2 rounded-full flex-none", dotFor(c.cover_color))} />
                    <span className="truncate">{c.title}</span>
                  </button>
                ))}
              </div>
            );
          })}
        </div>

        {/* Timeline scroller */}
        <div ref={scrollerRef} className="flex-1 overflow-auto kanban-scroll relative">
          <div style={{ width: rangeDays * DAY_WIDTH, minHeight: HEADER_HEIGHT + timelineHeight }} className="relative">
            {/* Date header */}
            <DateHeader days={days} today={today} />

            {/* Today vertical line */}
            <TodayLine anchor={anchor} today={today} rangeDays={rangeDays} totalHeight={HEADER_HEIGHT + timelineHeight} />

            {/* Rows */}
            <div className="relative" style={{ marginTop: 0 }}>
              {listOrder.map(lid => {
                const grp = scheduled[lid];
                return (
                  <div key={lid}>
                    {/* group header row (empty timeline) */}
                    <div style={{ height: ROW_HEIGHT }} className="border-b border-white/20 dark:border-white/5 bg-white/20 dark:bg-slate-900/20" />
                    {grp.cards.map(c => (
                      <GanttRow
                        key={c.card_id}
                        card={c}
                        anchor={anchor}
                        rangeDays={rangeDays}
                        labels={labels}
                        onPointerDown={onPointerDown}
                        onOpenCard={onOpenCard}
                        onScheduleAtDay={null}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Unscheduled drawer */}
      {unscheduled.length > 0 && (
        <UnscheduledDrawer
          cards={unscheduled}
          onSchedule={(c) => scheduleCard(c, today)}
          onOpenCard={onOpenCard}
        />
      )}
    </div>
  );
}

function DateHeader({ days, today }) {
  // Group days by week to give a clean two-row header (week label + day cells).
  const weeks = useMemo(() => {
    const arr = [];
    let cur = null;
    days.forEach((d, idx) => {
      const monday = startOfDay(addDays(d, -((d.getDay() + 6) % 7)));
      const key = monday.toISOString();
      if (!cur || cur.key !== key) {
        cur = { key, label: format(monday, "MMM yyyy", { locale: es }), startIdx: idx, count: 1 };
        arr.push(cur);
      } else {
        cur.count++;
      }
    });
    return arr;
  }, [days]);

  return (
    <div className="sticky top-0 z-20 bg-white/70 dark:bg-slate-900/70 backdrop-blur-md border-b border-white/40 dark:border-white/10" style={{ height: HEADER_HEIGHT }}>
      <div className="flex" style={{ height: HEADER_HEIGHT / 2 }}>
        {weeks.map(w => (
          <div
            key={w.key}
            className="text-[11px] uppercase tracking-wider font-semibold text-blue-600 dark:text-blue-300 px-2 flex items-center border-r border-white/30 dark:border-white/10"
            style={{ width: w.count * DAY_WIDTH }}
          >
            {w.label}
          </div>
        ))}
      </div>
      <div className="flex" style={{ height: HEADER_HEIGHT / 2 }}>
        {days.map((d, i) => {
          const isToday = differenceInCalendarDays(d, today) === 0;
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          return (
            <div
              key={i}
              className={cn(
                "text-[10px] text-center flex flex-col items-center justify-center border-r border-white/30 dark:border-white/10",
                isWeekend && "bg-white/40 dark:bg-slate-900/40 text-muted-foreground",
                isToday && "bg-blue-500/15 text-blue-700 dark:text-blue-300 font-semibold"
              )}
              style={{ width: DAY_WIDTH }}
            >
              <span className="leading-tight">{format(d, "EEE", { locale: es }).slice(0, 1).toUpperCase()}</span>
              <span className="leading-tight font-medium">{d.getDate()}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TodayLine({ anchor, today, rangeDays, totalHeight }) {
  const offset = differenceInCalendarDays(today, anchor);
  if (offset < 0 || offset >= rangeDays) return null;
  return (
    <div
      className="absolute top-0 z-10 w-px bg-blue-500"
      style={{ left: offset * DAY_WIDTH + DAY_WIDTH / 2, height: totalHeight }}
    >
      <div className="absolute -top-1 -left-1 h-2 w-2 rounded-full bg-blue-500 shadow" />
    </div>
  );
}

function GanttRow({ card, anchor, rangeDays, labels, onPointerDown, onOpenCard }) {
  const startOffset = differenceInCalendarDays(card._start, anchor);
  const durationDays = differenceInCalendarDays(card._due, card._start) + 1;
  const visibleStart = Math.max(0, startOffset);
  const visibleEnd = Math.min(rangeDays, startOffset + durationDays);
  const isOutOfRange = visibleEnd <= 0 || visibleStart >= rangeDays;

  // Color from first label, fallback to cover_color, fallback to blue.
  const label = (card.labels || []).map(id => labels.find(l => l.label_id === id)).find(Boolean);
  const colorKey = label?.color || card.cover_color || "blue";
  const barBg = {
    indigo: "bg-blue-500",  blue: "bg-blue-500",   violet: "bg-violet-500",
    slate: "bg-slate-600",  pink: "bg-pink-500",   emerald: "bg-emerald-500",
    amber: "bg-amber-500",  sky: "bg-sky-500",     rose: "bg-rose-500",
    red: "bg-red-500",
  }[colorKey] || "bg-blue-500";

  return (
    <div style={{ height: ROW_HEIGHT }} className="relative border-b border-white/20 dark:border-white/5">
      {/* Per-day grid background */}
      <div className="absolute inset-0 flex pointer-events-none">
        {Array.from({ length: rangeDays }).map((_, i) => {
          const d = addDays(anchor, i);
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          return (
            <div
              key={i}
              className={cn("border-r border-white/15 dark:border-white/5", isWeekend && "bg-white/20 dark:bg-slate-900/20")}
              style={{ width: DAY_WIDTH }}
            />
          );
        })}
      </div>

      {!isOutOfRange && (
        <div
          data-bar-id={card.card_id}
          data-testid={`sprints-bar-${card.card_id}`}
          onPointerDown={(e) => onPointerDown(e, card, "move")}
          onClick={(e) => { e.stopPropagation(); onOpenCard?.(card.card_id); }}
          className={cn(
            "absolute top-1.5 h-7 rounded-lg shadow-md cursor-grab active:cursor-grabbing select-none flex items-center px-2 group transition-shadow hover:shadow-lg",
            barBg
          )}
          style={{
            transform: `translateX(${startOffset * DAY_WIDTH}px)`,
            width: Math.max(DAY_WIDTH * 0.6, durationDays * DAY_WIDTH - 2),
          }}
          title={`${card.title} · ${format(card._start, "d MMM", { locale: es })} → ${format(card._due, "d MMM", { locale: es })}`}
        >
          {/* Left resize handle */}
          <div
            onPointerDown={(e) => onPointerDown(e, card, "resize-l")}
            data-testid={`sprints-resize-l-${card.card_id}`}
            className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize rounded-l-lg hover:bg-black/15"
            aria-label="Cambiar fecha de inicio"
          />
          <span className="text-white text-xs font-medium truncate pointer-events-none px-1.5">
            {card.title}
          </span>
          {/* Right resize handle */}
          <div
            onPointerDown={(e) => onPointerDown(e, card, "resize-r")}
            data-testid={`sprints-resize-r-${card.card_id}`}
            className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize rounded-r-lg hover:bg-black/15"
            aria-label="Cambiar fecha de fin"
          />
        </div>
      )}
    </div>
  );
}

function UnscheduledDrawer({ cards, onSchedule, onOpenCard }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex-none border-t border-white/40 dark:border-white/10 bg-white/40 dark:bg-slate-900/40 backdrop-blur-md" data-testid="sprints-unscheduled">
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="sprints-unscheduled-toggle"
        className="w-full px-4 py-2.5 flex items-center gap-2 text-sm font-medium hover:bg-white/30 dark:hover:bg-white/5 transition"
      >
        <Inbox className="h-4 w-4 text-amber-500" />
        <span>Sin programar</span>
        <span className="text-xs text-muted-foreground">({cards.length})</span>
        <span className="ml-auto text-[11px] text-muted-foreground">
          {open ? "Ocultar" : "Mostrar"}
        </span>
      </button>
      {open && (
        <div className="max-h-44 overflow-y-auto kanban-scroll p-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {cards.map(c => (
            <div
              key={c.card_id}
              className="rounded-lg bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-white/10 p-2 text-sm flex items-center gap-2"
            >
              <button
                onClick={() => onOpenCard?.(c.card_id)}
                className="truncate flex-1 text-left hover:text-blue-600 dark:hover:text-blue-300 transition"
                data-testid={`sprints-unscheduled-${c.card_id}`}
              >
                <span className={cn("inline-block h-2 w-2 rounded-full mr-2", dotFor(c.cover_color))} />
                {c.title}
              </button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-[11px] gap-1"
                onClick={() => onSchedule(c)}
                data-testid={`sprints-schedule-${c.card_id}`}
              >
                <CalendarRange className="h-3.5 w-3.5" /> Programar
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
